import { BaseApi } from './base.api.js';
import { teamAssociationApiMethods } from './team-association.mixin.js';

export class ExerciseApi extends BaseApi {
    constructor(client) {
        super(client, '/exercises');
        Object.assign(this, teamAssociationApiMethods(client, this.basePath));
    }

    async getStats() {
        const response = await this.client.get(`${this.basePath}/stats`);
        return response.data;
    }

    // --- Related BCPs ---
    async getRelatedBcps(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/bcps`, { params });
        return response.data;
    }
    async addBcps(id, bcpIds) {
        const response = await this.client.post(`${this.basePath}/${id}/bcps`, { bcpIds });
        return response.data;
    }
    async removeBcp(id, bcpId) {
        const response = await this.client.delete(`${this.basePath}/${id}/bcps/${bcpId}`);
        return response.data;
    }

    // --- Participating Vendors ---
    async getRelatedVendors(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/vendors`, { params });
        return response.data;
    }
    async addVendors(id, vendorIds) {
        const response = await this.client.post(`${this.basePath}/${id}/vendors`, { vendorIds });
        return response.data;
    }
    async removeVendor(id, vendorId) {
        const response = await this.client.delete(`${this.basePath}/${id}/vendors/${vendorId}`);
        return response.data;
    }

    // --- Exercise Participants ---
    async getRelatedParticipants(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/participants`, { params });
        return response.data;
    }
    async addParticipants(id, participantIds) {
        const response = await this.client.post(`${this.basePath}/${id}/participants`, { participantIds });
        return response.data;
    }
    async removeParticipant(id, participantId) {
        const response = await this.client.delete(`${this.basePath}/${id}/participants/${participantId}`);
        return response.data;
    }

    // --- Related Assessments ---
    async getRelatedAssessments(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/assessments`, { params });
        return response.data;
    }
    async addAssessments(id, assessmentIds) {
        const response = await this.client.post(`${this.basePath}/${id}/assessments`, { assessmentIds });
        return response.data;
    }
    async removeAssessment(id, assessmentId) {
        const response = await this.client.delete(`${this.basePath}/${id}/assessments/${assessmentId}`);
        return response.data;
    }

    // --- Scenario Timelines ---
    async getRelatedTimelines(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/timelines`, { params });
        return response.data;
    }
    async addTimelines(id, data) {
        const response = await this.client.post(`${this.basePath}/${id}/timelines`, data);
        return response.data;
    }
    async removeTimeline(id, timelineId) {
        const response = await this.client.delete(`${this.basePath}/${id}/timelines/${timelineId}`);
        return response.data;
    }

    // --- Recovery Activities ---
    async getRelatedRecoveryActivities(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/recovery-activities`, { params });
        return response.data;
    }
    async addRecoveryActivities(id, activityIds) {
        const response = await this.client.post(`${this.basePath}/${id}/recovery-activities`, { activityIds });
        return response.data;
    }
    async removeRecoveryActivity(id, activityId) {
        const response = await this.client.delete(`${this.basePath}/${id}/recovery-activities/${activityId}`);
        return response.data;
    }
    async reorderRecoveryActivities(id, orderedActivityIds) {
        const response = await this.client.patch(`${this.basePath}/${id}/recovery-activities/order`, { orderedActivityIds });
        return response.data;
    }

    // --- Exercise Evaluations ---
    async getRelatedEvaluations(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/evaluations`, { params });
        return response.data;
    }
    async addEvaluations(id, evaluationIds) {
        const response = await this.client.post(`${this.basePath}/${id}/evaluations`, { evaluationIds });
        return response.data;
    }
    async removeEvaluation(id, evaluationId) {
        const response = await this.client.delete(`${this.basePath}/${id}/evaluations/${evaluationId}`);
        return response.data;
    }

    // --- Improvement Actions ---
    async getRelatedImprovementActions(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/improvement-actions`, { params });
        return response.data;
    }
    async getLinkableImprovementActions(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/improvement-actions/linkable`, { params });
        return response.data;
    }
    async addImprovementActions(id, actionIds, extra = {}) {
        const response = await this.client.post(`${this.basePath}/${id}/improvement-actions`, { actionIds, ...extra });
        return response.data;
    }
    async unlinkImprovementActions(id, actionIds) {
        const response = await this.client.post(`${this.basePath}/${id}/improvement-actions/unlink`, { actionIds });
        return response.data;
    }
    async removeImprovementAction(id, actionId) {
        const response = await this.client.delete(`${this.basePath}/${id}/improvement-actions/${actionId}`);
        return response.data;
    }

    async getPartyTemplate(params = {}) {
        const response = await this.client.get(`${this.basePath}/party-template`, { params });
        return response.data;
    }

    async getParty(id) {
        const response = await this.client.get(`${this.basePath}/${id}/party`);
        return response.data;
    }

    async syncParty(id, body) {
        const response = await this.client.put(`${this.basePath}/${id}/party`, body);
        return response.data;
    }

    async getRelatedLessonsLearned(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/lessons-learned`, { params });
        return response.data;
    }

    async addLessonsLearned(id, lessonIds) {
        const response = await this.client.post(`${this.basePath}/${id}/lessons-learned`, { lessonIds });
        return response.data;
    }

    async removeLessonLearned(id, lessonId) {
        const response = await this.client.delete(`${this.basePath}/${id}/lessons-learned/${lessonId}`);
        return response.data;
    }
}
